import axios from 'axios';
import { saveAs } from 'file-saver';
import React, { Component, Fragment, useState } from 'react';
import { DebounceInput } from 'react-debounce-input';
import injectSheet from 'react-jss';
import { connect } from 'react-redux';
import { Navigate } from 'react-router-dom-v5-compat';

import { sendInvitationEmail } from '~/api/login';
import { black1 } from '~/assets/shared-styles/general';
import { PAGE_START } from '~/common/constants/page';
import AdminConsolePageNavHeader from '~/components/Admin/AdminConsolePageNavHeader/AdminConsolePageNavHeader';
import ImportJobsPolling from '~/components/Admin/ImportJobsPolling/ImportJobsPolling';
import BulkImportUserWizard from '~/components/Admin/UsersPage/BulkUserImportWizard/BulkImportUserWizard';
import UserImportStatusBarList from '~/components/Admin/UsersPage/ImportStatusBarList/ImportStatusBarList';
import {
  createUser,
  deleteUser,
  downloadImportResultsExcelFile,
  exportUsersExcelFile,
  getClientUsers,
  importUsersListExcelFile,
  startImportUsersListProcessing,
  updateUser,
} from '~/components/Admin/UsersPage/UsersPage.data';
import styles from '~/components/Admin/UsersPage/UsersPage.styles';
import { trackSegment } from '~/components/SegmentAnalytics';
import EcCard from '~/components/Shared/EcCard/EcCard';
import EcModal from '~/components/Shared/EcModal/EcModal';
import EcPaginate from '~/components/Shared/EcPaginate/EcPaginate';
import { showToast } from '~/components/Shared/EcToast/showToast';
import EcUserTable from '~/components/Shared/EcUserTable/EcUserTable';
import withHover from '~/components/Shared/HOCs/withHover/withHover';
import BackIcon from '~/components/Shared/Icons/BackIcon';
import CheckmarkIcon from '~/components/Shared/Icons/CheckmarkIcon';
import LoadingSpinner from '~/components/Shared/Icons/LoadingSpinner';
import PlusIcon from '~/components/Shared/Icons/PlusIcon';
import { Button } from '~/eds/src/components/Button/Button';
import { Markdown } from '~/eds/src/components/Markdown/Markdown';
import { Modal } from '~/eds/src/components/Modal/Modal';
import { BulkImportUserType } from '~/enums/BulkImportUserType';
import { CodedErrorCodeType } from '~/enums/CodedErrorCodeType';
import { FeatureFlagType } from '~/enums/FeatureFlagType';
import { QueryParamType } from '~/enums/QueryParamType';
import { UserRoleType } from '~/enums/UserRoleType';
import { OnboardingIdType } from '~/features/onboarding/OnboardingIdType';
import { FlagType } from '~/flags/flags';
import { withFlags } from '~/flags/withFlags';
import { injectIntl } from 'react-intl';
import { withFormatDate } from '~/i18n/hocs/withFormatDate';
import { testHasFlag } from '~/permissions/utils/testHasFlag';
import { RoutePathType } from '~/routing/enums';
import { withRouting } from '~/routing/withRouting';
import {
  MODAL_ADMIN_USER,
  MODAL_DELETE,
  MODAL_DEPARTMENT_TREE,
  MODAL_SEND_EMAIL,
} from '~/types/modal.types';
import { ERROR, SUCCESS } from '~/types/toast.types';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import { parseApiError } from '~/utils/parseApiError';
import {
  getPageSearchQueryByKey,
  updatePageSearchQuery,
} from '~/utils/searchQuery';
import { createPassword, getClientInfo, getUserClientInfo } from '~/utils/user';

const BackIconWithHover = withHover(BackIcon);
const PAGE_SIZE = 10; //BE defines

const getPositionName = (positionName) => {
  if (positionName === 'Power User - Edit Capabilites') return 'Power User';
  if (positionName === 'Workflow Only') return 'Workflow Only User';
  return positionName;
};

const getClientId = (props) => {
  const {
    params: { clientId },
  } = props;

  return clientId;
};

class Page extends Component {
  constructor(props) {
    super(props);

    this.navigateBack = this.navigateBack.bind(this);
    this.onUserCreate = this.onUserCreate.bind(this);
    this.onUserUpdate = this.onUserUpdate.bind(this);
    this.onUserDelete = this.onUserDelete.bind(this);
    this.handleNameChange = this.handleNameChange.bind(this);
    this.handlePasswordReset = this.handlePasswordReset.bind(this);
    this.handlePageClick = this.handlePageClick.bind(this);
    this.handleShowModal = this.handleShowModal.bind(this);
    this.handleShowSecondaryModal = this.handleShowSecondaryModal.bind(this);
    this.handleHideModal = this.handleHideModal.bind(this);
    this.handleHideSecondaryModal = this.handleHideSecondaryModal.bind(this);
    this.handleDepartmentChange = this.handleDepartmentChange.bind(this);
    this.handleFormFieldsUpdate = this.handleFormFieldsUpdate.bind(this);
    this.importStatusRequestSource = axios.CancelToken.source();

    this.state = {
      searchText: '',
      page: 1,
      usersListTotalCount: 0,
      usersList: [],
      departmentTree: null,
      departmentsSelected: [],
      currentModal: null,
      secondaryModal: null,
      selectedUser: null,
      errorMessage: '',
      newUserData: this.getDefaultUserData(),
      importStep: BulkImportUserType.Upload,
      importStepLoading: false,
      importSummaryData: null,
      showImportWizard: false,
      isUpdatingUser: false,
    };
  }

  componentDidMount() {
    const urlParams = {
      page: getPageSearchQueryByKey(QueryParamType.Page, PAGE_START),
      searchText: getPageSearchQueryByKey(QueryParamType.SearchText, ''),
    };
    this.setState({ ...urlParams }, () => {
      this.loadUserList();
      updatePageSearchQuery({
        [QueryParamType.Page]: urlParams.page,
        [QueryParamType.SearchText]: urlParams.searchText,
      });
    });
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      prevState.selectedUser !== this.state.selectedUser &&
      this.state.selectedUser
    ) {
      this.setState({
        departmentsSelected: this.state.selectedUser.departments,
      });
    }
  }

  getClientId = () => {
    return getClientId(this.props);
  };

  loadUserList() {
    const { page, searchText } = this.state;

    this.setState({ loading: true });

    getClientUsers(this.getClientId(), page, searchText)
      .then((res) => {
        let list = [];
        res.results.map((item) => {
          const position = getPositionName(item.position_name);

          const positionName =
            position +
            (item.is_workflow_admin ? ' • Workflow Admin' : '') +
            (item.is_automation_admin ? ' • Automation Admin' : '');

          return list.push({
            id: item.id,
            email: item.email,
            firstName: item.first_name,
            lastName: item.last_name,
            lastLogin: item.last_login,
            role: item.role,
            positionName,
            password: item.password,
            departments: item.departments,
            departmentsSelected: item.departments,
            jobTitle: item.job_title,
            workflowAdmin: item.is_workflow_admin,
            automationAdmin: item.is_automation_admin,
          });
        });

        this.setState({
          usersList: list,
          usersListTotalCount: res.count,
          loading: false,
          errorLoadingUsers: false,
        });
      })
      .catch((err) => {
        if (err.response && err.response.status === 403) {
          this.setState({
            loading: false,
            errorLoadingUsers: false,
            noAccess: true,
          });
        } else {
          this.setState({ loading: false, errorLoadingUsers: true });
        }
      });
  }

  navigateBack() {
    this.props.navigate(-1);
  }

  getDefaultUserData() {
    return {
      firstName: '',
      lastName: '',
      email: '',
      role: UserRoleType.PowerUserWithEdit,
      password: '',
      departments: [],
      workflowAdmin: false,
      automationAdmin: false,
      shouldSendEmail: true,
    };
  }

  onUserCreate = async (
    email,
    firstName,
    lastName,
    role,
    password,
    jobTitle,
    workflowAdmin,
    automationAdmin,
    shouldSendEmail = false,
  ) => {
    const { currentUser, intl, formatDate } = this.props;
    let first_password = password || this.state.newUserData.password;
    const { departmentsSelected, selectedUser } = this.state;
    const departmentsToSubmit = departmentsSelected
      ? departmentsSelected?.map((d) => d.department_id)
      : selectedUser
        ? selectedUser.departmentsSelected?.map((d) => d.department_id)
        : [];

    try {
      const res = await createUser(
        this.getClientId(),
        email,
        firstName,
        lastName,
        jobTitle,
        role,
        first_password,
        departmentsToSubmit,
        workflowAdmin,
        automationAdmin,
      );

      if (shouldSendEmail) {
        this.handlePasswordReset(res);
      }

      this.handleHideModal();
      this.setState({
        errorMessage: '',
        newUserData: this.getDefaultUserData(),
      });
      showToast(
        SUCCESS,
        intl.formatMessage(
          {
            id: 'AdminUsers.userAddedSuccess',
            defaultMessage:
              'User "{firstName} {lastName}" has been added to the system.',
            description:
              'Success message when user is added. Example: firstName = "John", lastName = "Doe"',
          },
          {
            firstName: res.first_name,
            lastName: res.last_name,
          },
        ),
      );
      this.loadUserList();

      trackSegment('New User Created', {
        shouldSendEmail,
        groupId: getClientInfo(currentUser),
        userId: getUserClientInfo(currentUser),
        userCreatedDate: formatDate(new Date(), 'utc'),
        userLoginEmailSentDate: shouldSendEmail
          ? formatDate(new Date(), 'utc')
          : '',
      });
    } catch (err) {
      if (err.response) {
        const response = JSON.parse(err.response.request.response);
        if (response['non_field_errors']) {
          this.handleAddUserError(
            intl.formatMessage({
              id: 'AdminUsers.unregisteredEmailError',
              defaultMessage: 'Please enter an unregistered email.',
              description: 'Error message when email is already registered',
            }),
            firstName,
            lastName,
            email,
            role,
            password,
          );
        } else if (response['email']) {
          this.handleAddUserError(
            intl.formatMessage({
              id: 'AdminUsers.validEmailError',
              defaultMessage: 'Please enter a valid email address.',
              description: 'Error message when email format is invalid',
            }),
            firstName,
            lastName,
            email,
            role,
            password,
          );
        } else if (err.response.request.response.startsWith('["password ')) {
          this.handleAddUserError(
            intl.formatMessage({
              id: 'AdminUsers.passwordRequirementsError',
              defaultMessage:
                'Please enter a password that meets the requirements.',
              description:
                'Error message when password does not meet requirements',
            }),
            firstName,
            lastName,
            email,
            role,
            password,
          );
        } else {
          this.handleAddUserError(
            intl.formatMessage({
              id: 'AdminUsers.checkValuesError',
              defaultMessage: 'Please check the values you entered.',
              description: 'Generic error message for form validation',
            }),
            firstName,
            lastName,
            email,
            role,
            password,
          );
        }
      }
    }
  };

  onUserUpdate(
    email,
    firstName,
    lastName,
    role,
    password,
    jobTitle,
    workflowAdmin,
    automationAdmin,
  ) {
    const { currentUser, intl } = this.props;
    const { selectedUser, departmentsSelected } = this.state;
    const departmentsToSubmit = departmentsSelected
      ? departmentsSelected?.map((d) => d.department_id)
      : selectedUser
        ? selectedUser.departmentsSelected?.map((d) => d.department_id)
        : [];

    updateUser(
      selectedUser.id,
      email,
      firstName,
      lastName,
      role,
      departmentsToSubmit,
      jobTitle,
      workflowAdmin,
      automationAdmin,
    )
      .then(() => {
        showToast(
          SUCCESS,
          intl.formatMessage(
            {
              id: 'AdminUsers.userUpdatedSuccess',
              defaultMessage:
                'User information for "{firstName} {lastName}" has been updated.',
              description:
                'Success message when user is updated. Example: firstName = "John", lastName = "Doe"',
            },
            {
              firstName: selectedUser.firstName,
              lastName: selectedUser.lastName,
            },
          ),
        );
        this.handleHideModal();
        this.loadUserList();
        if (selectedUser.id === currentUser.id) {
          window.location.reload();
        }
      })
      .catch((err) => {
        const response = JSON.parse(err.response.request.response);
        if (response['non_field_errors']) {
          this.handleAddUserError(
            intl.formatMessage({
              id: 'AdminUsers.unregisteredEmailError',
              defaultMessage: 'Please enter an unregistered email.',
              description: 'Error message when email is already registered',
            }),
            firstName,
            lastName,
            email,
            role,
          );
        } else if (response['email']) {
          this.handleAddUserError(
            intl.formatMessage({
              id: 'AdminUsers.validEmailError',
              defaultMessage: 'Please enter a valid email address.',
              description: 'Error message when email format is invalid',
            }),
            firstName,
            lastName,
            email,
            role,
          );
        } else {
          this.handleAddUserError(
            intl.formatMessage({
              id: 'AdminUsers.checkValuesError',
              defaultMessage: 'Please check the values you entered.',
              description: 'Generic error message for form validation',
            }),
            firstName,
            lastName,
            email,
            role,
          );
        }
      });
  }

  onUserDelete() {
    const { currentUser, intl, formatDate } = this.props;
    const { selectedUser } = this.state;

    deleteUser(selectedUser.id)
      .then(() => {
        let newUsersList = [...this.state.usersList];
        newUsersList = newUsersList.filter(
          (user) => user.id !== selectedUser.id,
        );
        showToast(
          SUCCESS,
          intl.formatMessage(
            {
              id: 'AdminUsers.userRemovedSuccess',
              defaultMessage:
                '"{firstName} {lastName}" has been removed from the system.',
              description:
                'Success message when user is removed. Example: firstName = "John", lastName = "Doe"',
            },
            {
              firstName: selectedUser.firstName,
              lastName: selectedUser.lastName,
            },
          ),
        );
        this.setState({ usersList: newUsersList });
        trackSegment('User Deleted', {
          groupId: getClientInfo(currentUser),
          userId: getUserClientInfo(currentUser),
          userIdDeleted: selectedUser.id,
          userDeletedDate: formatDate(new Date(), 'utc'),
        });
      })
      .catch((err) => showToast(ERROR, err.message));
  }

  handleNameChange(
    email,
    firstName,
    lastName,
    role,
    password,
    jobTitle,
    workflowAdmin,
    automationAdmin,
    shouldSendEmail = false,
  ) {
    const { forEditing, isUpdatingUser } = this.state;
    if (isUpdatingUser) return;

    const callback = forEditing ? this.onUserUpdate : this.onUserCreate;
    this.setState({ isUpdatingUser: true }, () =>
      callback(
        email,
        firstName,
        lastName,
        role,
        password,
        jobTitle,
        workflowAdmin,
        automationAdmin,
        shouldSendEmail,
      ),
    );
  }

  handleShowModal(modal, forEditing, selectedUser = null) {
    this.setState({
      currentModal: modal,
      forEditing,
      selectedUser,
    });
  }

  handleHideModal() {
    this.setState({
      currentModal: null,
      selectedUser: null,
      departmentsSelected: null,
      newUserData: {
        firstName: '',
        lastName: '',
        email: '',
        role: UserRoleType.PowerUserWithEdit,
        password: '',
        departments: [],
        departmentsSelected: null,
        jobTitle: '',
        shouldSendEmail: true,
      },
      errorMessage: '',
      importStep: BulkImportUserType.Upload,
      importStepLoading: false,
      isUpdatingUser: false,
    });
  }

  handlePageClick(clickedPage) {
    const { searchText } = this.state;
    const selectedPage = clickedPage.selected + 1;

    this.setState({ page: selectedPage, loading: true }, () => {
      this.loadUserList();
      updatePageSearchQuery(
        {
          [QueryParamType.Page]: selectedPage,
          [QueryParamType.SearchText]: searchText,
        },
        true,
      );
    });
  }

  handleInputChangeSearchText = (e) => {
    const { value } = e.target;

    this.setState({ searchText: value, page: 1 }, () => {
      this.loadUserList();
      updatePageSearchQuery({
        [QueryParamType.Page]: 1,
        [QueryParamType.SearchText]: value,
      });
    });
  };

  handleAddUserError(
    errorMessage,
    firstName = '',
    lastName = '',
    email = '',
    role = 1,
    password = '',
    workflowAdmin = false,
    automationAdmin = false,
  ) {
    const newUserData = {
      firstName,
      lastName,
      email,
      role,
      password,
      workflowAdmin,
      automationAdmin,
    };

    this.setState({ errorMessage, newUserData, isUpdatingUser: false });
  }

  handlePasswordReset = async (user) => {
    const { currentUser, intl, formatDate } = this.props;
    const selectedUser = user || this.state.selectedUser;
    try {
      await sendInvitationEmail({ userId: selectedUser.id });
      showToast(
        SUCCESS,
        intl.formatMessage(
          {
            id: 'AdminUsers.resetPasswordEmailSent',
            defaultMessage: 'We have sent a reset password link to "{email}"',
            description:
              'Success message when password reset email is sent. Example: email = "user@example.com"',
          },
          {
            email: selectedUser.email,
          },
        ),
      );
      trackSegment('Admin Sent Reset Password Email', {
        groupId: getClientInfo(currentUser),
        userId: getUserClientInfo(currentUser),
        userIdResetPasswordSent: selectedUser.id,
        adminResetPasswordEmailSentDate: formatDate(new Date(), 'utc'),
      });
    } catch (err) {
      showToast(ERROR, err.message);
    }
  };

  handleFormFieldsUpdate(form) {
    this.setState({ selectedUser: form });
  }

  sortUsers() {
    let newUsersList = [...this.state.usersList];
    newUsersList.sort((a, b) => (a.id < b.id ? 1 : -1));

    return newUsersList;
  }

  renderUserModal() {
    const { intl } = this.props;
    const {
      selectedUser,
      newUserData,
      errorMessage,
      departmentsSelected,
      forEditing,
      isUpdatingUser,
    } = this.state;
    const newUserPassword = createPassword();

    const selectedDept = departmentsSelected
      ? departmentsSelected
      : selectedUser
        ? selectedUser.departmentsSelected
        : [];

    return (
      <EcModal
        modalType={MODAL_ADMIN_USER}
        width="560px"
        formFields={{
          email: selectedUser ? selectedUser.email : newUserData.email,
          firstName: selectedUser
            ? selectedUser.firstName
            : newUserData.firstName,
          lastName: selectedUser ? selectedUser.lastName : newUserData.lastName,
          password: selectedUser ? selectedUser.password : newUserPassword,
          departmentsSelected: selectedDept,
          id: selectedUser ? selectedUser.id : null,
          role: selectedUser ? selectedUser.role : newUserData.role,
          jobTitle: selectedUser ? selectedUser.jobTitle : newUserData.jobTitle,
          workflowAdmin: selectedUser
            ? selectedUser.workflowAdmin
            : newUserData.workflowAdmin,
          automationAdmin: selectedUser
            ? selectedUser.automationAdmin
            : newUserData.automationAdmin,
          shouldSendEmail: selectedUser
            ? selectedUser.shouldSendEmail
            : newUserData.shouldSendEmail,
        }}
        title={
          forEditing
            ? intl.formatMessage({
                id: 'AdminUsers.editUserTitle',
                defaultMessage: 'Edit User',
                description: 'Title for edit user modal',
              })
            : intl.formatMessage({
                id: 'AdminUsers.addUserTitle',
                defaultMessage: 'Add User',
                description: 'Title for add user modal',
              })
        }
        confirmButtonIcon={
          forEditing ? null : <PlusIcon size="20" color={black1} />
        }
        confirmButtonText={
          forEditing
            ? intl.formatMessage({
                id: 'AdminUsers.saveButton',
                defaultMessage: 'Save',
                description: 'Save button text',
              })
            : intl.formatMessage({
                id: 'AdminUsers.createUserButton',
                defaultMessage: 'Create User',
                description: 'Create user button text',
              })
        }
        confirmButtonDisabled={isUpdatingUser}
        onUserFormUpdate={this.handleFormFieldsUpdate}
        handleNameChange={this.handleNameChange}
        hideModal={this.handleHideModal}
        handleShowModal={this.handleShowModal}
        handleShowSecondaryModal={this.handleShowSecondaryModal}
        errorMessage={errorMessage}
        forEditing={forEditing}
        clientId={this.getClientId()}
      />
    );
  }

  handleShowSecondaryModal(modal) {
    this.setState({ secondaryModal: modal });
  }

  handleHideSecondaryModal() {
    this.setState({ secondaryModal: null });
  }

  handleDepartmentChange(departmentsSelected) {
    this.setState({ departmentsSelected });
  }

  handleUserDownloadClick = (onlyHeaders) => {
    const { intl, formatDate } = this.props;
    let filename = 'evisort-users-import-template.xlsx';
    const timestamp = formatDate(new Date(), 'utc');

    if (!onlyHeaders) {
      filename = `evisort-users-${timestamp}.xlsx`;
    }

    exportUsersExcelFile({ clientId: this.getClientId(), onlyHeaders })
      .then((res) => {
        showToast(
          SUCCESS,
          intl.formatMessage({
            id: 'AdminUsers.fileDownloadedSuccess',
            defaultMessage: 'File downloaded successfully.',
            description: 'Success message when file is downloaded',
          }),
        );
        saveAs(res, filename);
      })
      .catch((errorResponse) => {
        this.handleApiError(errorResponse);
      });
  };

  handleUserListUploadClick = (file) => {
    const { intl } = this.props;
    this.setState({ importStepLoading: true });

    const formData = new FormData();
    formData.append('file', file);
    formData.append('client_id', this.getClientId());

    importUsersListExcelFile(formData)
      .then((response) => {
        showToast(
          SUCCESS,
          intl.formatMessage({
            id: 'AdminUsers.usersListImportedSuccess',
            defaultMessage: 'Users List file has been imported successfully.',
            description: 'Success message when users list is imported',
          }),
        );
        this.setState({
          importStep: BulkImportUserType.Summary,
          importStepLoading: false,
          importSummaryData: response,
        });
      })
      .catch((errorResponse) => {
        this.handleApiError(
          errorResponse,
          CodedErrorCodeType.BulkImportUserErrorSubmittingFile,
        );
        this.setState({
          importStep: BulkImportUserType.Upload,
          importStepLoading: false,
        });
      });
  };

  handleSelectedImportStep = (importStep) => {
    this.setState({ importStep });
  };

  handleImportResultsFileDownloadClick = (resultType) => {
    const { intl, formatDate } = this.props;

    const importId = this.state.importSummaryData.import_id;

    downloadImportResultsExcelFile({
      clientId: this.getClientId(),
      importId,
      resultType,
    })
      .then((res) => {
        showToast(
          SUCCESS,
          intl.formatMessage(
            {
              id: 'AdminUsers.resultFileDownloadedSuccess',
              defaultMessage: '{resultType} file downloaded successfully.',
              description:
                'Success message when result file is downloaded. Example: resultType = "success", "error"',
            },
            {
              resultType,
            },
          ),
        );

        const timestamp = formatDate(new Date(), 'utc');
        saveAs(res, `evisort-users-${resultType}-${timestamp}.xlsx`);
      })
      .catch((errorResponse) => {
        this.handleApiError(
          errorResponse,
          CodedErrorCodeType.BulkImportErrorDownloadingJobResult,
          { resultType },
        );
      });
  };

  handleStartImportUserBulkProcessing = (importActions, hideModalFn) => {
    const { intl } = this.props;
    this.setState({ importStepLoading: true });
    startImportUsersListProcessing({
      client_id: this.getClientId(),
      import_id: this.state.importSummaryData.import_id,
      email_new_users: importActions.emailNewUsers,
      update_existing_users: importActions.updateExistingUsers,
      delete_missing_users: importActions.deleteMissingUsers,
    })
      .then(() => {
        showToast(
          SUCCESS,
          intl.formatMessage({
            id: 'AdminUsers.importProcessingQueuedSuccess',
            defaultMessage:
              'Request to start Import Users List processing successfully queued.',
            description: 'Success message when import processing is queued',
          }),
        );
        this.props.setImportJobId(this.state.importSummaryData.import_id);
        this.handleHideImportWizard();
        hideModalFn();
      })
      .catch((errorResponse) => {
        this.handleApiError(
          errorResponse,
          CodedErrorCodeType.BulkImportUserErrorQueuingJob,
        );
        this.setState({ importStepLoading: false });
      });
  };

  handleShowImportWizard = () => {
    this.setState({ showImportWizard: true });
  };

  handleHideImportWizard = () => {
    this.setState({
      showImportWizard: false,
      importStep: BulkImportUserType.Upload,
      importStepLoading: false,
    });
  };

  handleApiError = (errorResponse) => {
    const error = parseApiError(errorResponse);
    showToast(ERROR, error.message);
  };

  renderDepartmentTreeModal() {
    const { intl } = this.props;
    const { departmentTree, selectedUser, departmentsSelected } = this.state;
    const deptsSelected = departmentsSelected
      ? departmentsSelected
      : selectedUser
        ? selectedUser.departments
        : [];

    return (
      <EcModal
        modalType={MODAL_DEPARTMENT_TREE}
        width="540px"
        title={intl.formatMessage({
          id: 'AdminUsers.selectDepartmentTitle',
          defaultMessage: 'Select a Department',
          description: 'Title for department selection modal',
        })}
        client={this.getClientId()}
        departmentTree={departmentTree}
        departmentsSelected={deptsSelected}
        handleDepartmentChange={this.handleDepartmentChange}
        confirmButtonIcon={<CheckmarkIcon size="20" />}
        confirmButtonText={intl.formatMessage({
          id: 'AdminUsers.selectDepartmentButton',
          defaultMessage: 'Select Department',
          description: 'Button text to select department',
        })}
        hideModal={this.handleHideSecondaryModal}
      />
    );
  }

  renderSendPasswordEmailModalText() {
    const { intl } = this.props;
    const { selectedUser } = this.state;

    return (
      <Fragment>
        {intl.formatMessage(
          {
            id: 'AdminUsers.sendPasswordEmailConfirmation',
            defaultMessage:
              'Are you sure you want to send? Clicking this button will send an email to the user {firstName} {lastName} at ({email}) with a link to set a new password.',
            description:
              'Confirmation text for sending password reset email. Example: firstName = "John", lastName = "Doe", email = "john@example.com"',
          },
          {
            firstName: selectedUser.firstName,
            lastName: selectedUser.lastName,
            email: selectedUser.email,
          },
        )}
      </Fragment>
    );
  }

  renderSendPasswordEmailModal() {
    const { intl } = this.props;
    return (
      <EcModal
        modalType={MODAL_SEND_EMAIL}
        width="560px"
        title={intl.formatMessage({
          id: 'AdminUsers.sendPasswordLinkTitle',
          defaultMessage: 'Send Link to Set New Password',
          description: 'Title for send password reset link modal',
        })}
        text={this.renderSendPasswordEmailModalText()}
        confirmButtonText={intl.formatMessage({
          id: 'AdminUsers.sendLinkButton',
          defaultMessage: 'Send Link',
          description: 'Button text to send password reset link',
        })}
        hideModal={this.handleHideModal}
        resetPassword={this.handlePasswordReset}
      />
    );
  }

  renderDeleteModal() {
    const { intl } = this.props;
    const { selectedUser } = this.state;
    return (
      <Modal
        children={
          <Markdown
            text={intl.formatMessage(
              {
                id: 'AdminUsers.deleteUserConfirmation',
                defaultMessage:
                  "Are you sure you want to delete {firstName} {lastName}? You won't be able to undo this action.",
                description:
                  'Confirmation message for deleting user. Example: firstName = "John", lastName = "Doe"',
              },
              {
                firstName: selectedUser.firstName,
                lastName: selectedUser.lastName,
              },
            )}
          />
        }
        title={intl.formatMessage({
          id: 'AdminUsers.deleteUserTitle',
          defaultMessage: 'Delete Selected User?',
          description: 'Title for delete user modal',
        })}
        isVisible
        onHide={this.handleHideModal}
        onCancel={this.handleHideModal}
        primaryAction={{
          text: intl.formatMessage({
            id: 'AdminUsers.deleteButton',
            defaultMessage: 'Delete',
            description: 'Delete button text',
          }),
          variant: 'danger',
          onClick: () => {
            this.onUserDelete();
            this.handleHideModal();
          },
        }}
      />
    );
  }

  renderBulkImportUserWizard = () => {
    const { importStep, importStepLoading, importSummaryData } = this.state;
    return (
      <BulkImportUserWizard
        handleDownloadUserTemplateFile={this.handleUserDownloadClick}
        handleImportResultsFileDownload={
          this.handleImportResultsFileDownloadClick
        }
        handleSelectedStep={this.handleSelectedImportStep}
        onClose={this.handleHideImportWizard}
        importSummaryData={importSummaryData}
        onUploadActionClick={this.handleUserListUploadClick}
        step={importStep}
        stepLoading={importStepLoading}
        onStartImportUserBulkProcessing={
          this.handleStartImportUserBulkProcessing
        }
      />
    );
  };

  renderPagination() {
    const { classes } = this.props;
    const { page, usersListTotalCount } = this.state;

    return usersListTotalCount > PAGE_SIZE ? (
      <div className={classes.paginationWrapper}>
        <EcPaginate
          onPageChange={this.handlePageClick}
          pageCount={Math.ceil(usersListTotalCount / PAGE_SIZE)}
          forcePage={page - 1}
        />
      </div>
    ) : null;
  }

  renderUsersContent() {
    const { usersList } = this.state;
    const sortedUsers = this.sortUsers(usersList);

    return (
      <Fragment>
        <EcCard contentStyles={{ display: 'flex', padding: '0 16px' }}>
          <EcUserTable
            listItems={sortedUsers}
            handleShowModal={this.handleShowModal}
          />
        </EcCard>
        {this.renderPagination()}
      </Fragment>
    );
  }

  renderNavHeader() {
    const { classes, intl } = this.props;

    return (
      <div className={classes.pageHeader}>
        <button
          className={classes.pageBackAction}
          onClick={() => this.navigateBack()}
        >
          <BackIconWithHover />
        </button>
        <div className={classes.pageTitle}>
          <span className={classes.subTitle}>
            {intl.formatMessage({
              id: 'AdminUsers.subTitle',
              defaultMessage: 'ADMIN CONSOLE',
              description: 'Admin console subtitle',
            })}
          </span>
          <h2 className={classes.title}>
            {intl.formatMessage({
              id: 'AdminUsers.pageTitle',
              defaultMessage: 'Users',
              description: 'Users page title',
            })}
          </h2>
        </div>
      </div>
    );
  }

  render() {
    const {
      classes,
      importJobsData = [],
      handleAcknowledgeImportJobSuccessUpdate = () => {},
      flags,
      intl,
    } = this.props;
    const {
      loading,
      errorLoadingUsers,
      noAccess,
      searchText,
      currentModal,
      secondaryModal,
      showImportWizard,
    } = this.state;

    if (flags[FlagType.NewUsersPage]) {
      return (
        <Navigate
          to={RoutePathType.AdminUsers.replace(':clientId', this.getClientId())}
          replace
        />
      );
    }

    const hasBulkUserFeatureFlag = testHasFlag(FeatureFlagType.BulkUser)(
      this.props.currentUser,
    );

    if (loading && !searchText) {
      return (
        <div className={classes.loadingContainer}>
          <LoadingSpinner size="medium" />
        </div>
      );
    } else if (noAccess) {
      return (
        <div>
          <p>
            {intl.formatMessage({
              id: 'AdminUsers.noAccessMessage',
              defaultMessage: 'You do not have permission to access this page.',
              description: 'No access permission message',
            })}
          </p>
          <p>
            {intl.formatMessage({
              id: 'AdminUsers.contactAdminMessage',
              defaultMessage:
                'To request permission, contact your organization administrator.',
              description: 'Contact admin message for permissions',
            })}
          </p>
        </div>
      );
    } else if (errorLoadingUsers) {
      return (
        <p>
          {intl.formatMessage({
            id: 'AdminUsers.errorLoadingMessage',
            defaultMessage: 'An error occurred while loading this page.',
            description: 'Error loading page message',
          })}
        </p>
      );
    }

    return (
      <div className={classes.adminUsersTabContent}>
        <AdminConsolePageNavHeader
          title={intl.formatMessage({
            id: 'AdminUsers.pageTitle',
            defaultMessage: 'Users',
            description: 'Users page title',
          })}
          navigateBack={this.navigateBack}
        />
        <FlexLayout justifyContent="space-between" space={4}>
          <div className={classes.userSearchRow}>
            <div
              className={classes.userSearchContainer}
              data-testid="search-by-user-name"
            >
              <div className={classes.userSearchHeader}>
                {intl.formatMessage({
                  id: 'AdminUsers.searchByUserNameHeader',
                  defaultMessage: 'SEARCH BY USER NAME',
                  description: 'Header for search by user name section',
                })}
              </div>
              <DebounceInput
                autoFocus={true}
                debounceTimeout={500}
                id="admin_searchByUserName"
                placeholder={intl.formatMessage({
                  id: 'AdminUsers.searchByUserNamePlaceholder',
                  defaultMessage: 'Search by user name',
                  description: 'Placeholder text for user name search input',
                })}
                type="text"
                value={searchText}
                onChange={this.handleInputChangeSearchText}
              />
            </div>
          </div>
          <FlexLayout space={4} py={4}>
            {hasBulkUserFeatureFlag && (
              <Button
                text={intl.formatMessage({
                  id: 'AdminUsers.exportUsersButton',
                  defaultMessage: 'Export Users',
                  description: 'Export users button text',
                })}
                onClick={() => this.handleUserDownloadClick(false)}
              />
            )}
            {hasBulkUserFeatureFlag && (
              <Button
                id={OnboardingIdType.ImportNewUser}
                text={intl.formatMessage({
                  id: 'AdminUsers.importUsersButton',
                  defaultMessage: 'Import Users',
                  description: 'Import users button text',
                })}
                onClick={() => this.handleShowImportWizard()}
              />
            )}
            <Button
              ariahaspopup
              id={OnboardingIdType.AddNewUser}
              text={intl.formatMessage({
                id: 'AdminUsers.addNewUserButton',
                defaultMessage: 'Add New User',
                description: 'Add new user button text',
              })}
              variant="primary"
              onClick={() => this.handleShowModal(MODAL_ADMIN_USER, false)}
            />
          </FlexLayout>
        </FlexLayout>

        {hasBulkUserFeatureFlag && (
          <UserImportStatusBarList
            importJobsData={importJobsData}
            clientId={this.getClientId()}
            handleAcknowledgeImportJobSuccessUpdate={
              handleAcknowledgeImportJobSuccessUpdate
            }
          />
        )}

        {this.renderUsersContent()}

        {currentModal === MODAL_ADMIN_USER ? this.renderUserModal() : null}
        {currentModal === MODAL_SEND_EMAIL
          ? this.renderSendPasswordEmailModal()
          : null}
        {currentModal === MODAL_DELETE ? this.renderDeleteModal() : null}
        {secondaryModal === MODAL_DEPARTMENT_TREE
          ? this.renderDepartmentTreeModal()
          : null}

        {showImportWizard && this.renderBulkImportUserWizard()}
      </div>
    );
  }
}

const mapStateToProps = ({ currentUser }) => ({ currentUser });

const PageWithPolling = (props) => {
  const [importedJobId, setImportJobId] = useState(null);
  return (
    <ImportJobsPolling
      clientId={getClientId(props)}
      importedJobId={importedJobId}
      jobsKind="user"
      nullifyImportedJobIdProp={() => setImportJobId(null)}
    >
      <Page {...{ ...props, setImportJobId }} />
    </ImportJobsPolling>
  );
};

const PageWithPollingAndState = connect(mapStateToProps)(
  withFlags(withRouting(injectIntl(PageWithPolling))),
);

export default injectSheet(styles)(
  injectIntl(withFormatDate(PageWithPollingAndState)),
);
